import { ArrowUpRight } from "lucide-react";
import { FadeUp } from "@/components/motion/Reveal";
import { WORK } from "@/data/content";

const CardInner = ({ item, featured }) => (
  <>
    {/* accent top line on hover */}
    <span
      className="pointer-events-none absolute left-0 top-0 z-20 h-1 w-full origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100"
      style={{ background: item.accent }}
    />

    <div className={`grid ${featured ? "md:grid-cols-2" : "grid-cols-1"}`}>
      {/* Image */}
      <div className={`relative overflow-hidden ${featured ? "aspect-[16/11] md:aspect-auto md:min-h-[380px]" : "aspect-[16/10]"}`}>
        <img
          src={item.img}
          alt={`${item.title} — ${item.label}`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
          style={item.slug === "prime-improvements" ? { objectPosition: "50% 62%" } : undefined}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
        {/* highlighted category badge (e.g. cosmetics) */}
        {item.highlightLabel && (
          <span
            className="absolute left-4 top-4 z-10 rounded-full px-3 py-1.5 font-mono text-[11px] font-medium uppercase tracking-[0.15em] text-ink"
            style={{ background: item.accent }}
          >
            {item.label}
          </span>
        )}
      </div>

      {/* Content */}
      <div className={`flex flex-col justify-between p-7 md:p-9 ${featured ? "md:justify-center" : ""}`}>
        <div>
          <div className="mb-4 flex items-center justify-between">
            <span className="font-mono text-xs" style={{ color: item.accent }}>{item.no}</span>
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ash">{item.year}</span>
          </div>

          {!item.highlightLabel && (
            <div className="mb-3 flex items-center gap-3">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: item.accent }} />
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ash">{item.label}</span>
            </div>
          )}

          <h3 className={`font-display font-semibold uppercase leading-[0.92] tracking-tightest text-chalk ${featured ? "text-5xl md:text-6xl" : "text-3xl md:text-4xl"}`}>
            {item.title}
          </h3>

          <p className="mt-2 font-mono text-[12px] uppercase tracking-[0.14em] text-signal">{item.role}</p>
          <p className={`mt-4 text-[15px] leading-[1.6] text-ash ${featured ? "max-w-lg" : "max-w-md"}`}>{item.desc}</p>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {item.tags.map((t) => (
            <span key={t} className="border border-line px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-ash">
              {t}
            </span>
          ))}
          <span className="ml-auto inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-chalk">
            {item.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </span>
        </div>
      </div>
    </div>
  </>
);

const ProjectCard = ({ item, onNavigate, featured }) => {
  const baseClass = `group relative block overflow-hidden border border-line bg-surface transition-colors duration-500 hover:border-white/40 ${
    featured ? "lg:col-span-12" : "lg:col-span-6"
  }`;

  if (item.href) {
    return (
      <FadeUp className={featured ? "lg:col-span-12" : "lg:col-span-6"}>
        <a
          href={item.href}
          onClick={(e) => { e.preventDefault(); onNavigate?.(item.href); }}
          data-testid={`work-tile-${item.slug}`}
          className={baseClass.replace(featured ? "lg:col-span-12" : "lg:col-span-6", "").trim() + " cursor-pointer"}
        >
          <CardInner item={item} featured={featured} />
        </a>
      </FadeUp>
    );
  }

  if (item.url) {
    return (
      <FadeUp className={featured ? "lg:col-span-12" : "lg:col-span-6"}>
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`work-tile-${item.slug}`}
          className={baseClass.replace(featured ? "lg:col-span-12" : "lg:col-span-6", "").trim() + " cursor-pointer"}
        >
          <CardInner item={item} featured={featured} />
        </a>
      </FadeUp>
    );
  }

  return (
    <FadeUp className={featured ? "lg:col-span-12" : "lg:col-span-6"}>
      <div data-testid={`work-tile-${item.slug}`} className={baseClass.replace(featured ? "lg:col-span-12" : "lg:col-span-6", "").trim()}>
        <CardInner item={item} featured={featured} />
      </div>
    </FadeUp>
  );
};

const Work = ({ onNavigate }) => {
  const featured = WORK.find((w) => w.featured);
  const rest = WORK.filter((w) => !w.featured);

  return (
    <section id="work" data-testid="work-section" className="border-t border-line bg-ink">
      <div className="mx-auto max-w-[1600px] scroll-mt-24 px-6 py-24 md:px-12 md:py-36">
        <FadeUp className="mb-12 flex items-end justify-between border-b border-line pb-8 md:mb-16">
          <div>
            <p className="mb-4 font-mono text-[12px] uppercase tracking-[0.2em] text-signal">/ Selected Work</p>
            <h2 className="font-display text-4xl font-medium uppercase leading-none tracking-tight text-chalk sm:text-5xl md:text-6xl">
              Brands &amp; products I&apos;ve built
            </h2>
          </div>
          <span className="hidden font-mono text-[12px] uppercase tracking-[0.2em] text-ash md:block">{WORK.length} Projects</span>
        </FadeUp>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {featured && <ProjectCard item={featured} onNavigate={onNavigate} featured />}
          {rest.map((item) => (
            <ProjectCard key={item.slug} item={item} onNavigate={onNavigate} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Work;
